/**
 * `canary spec` subcommand entry points.
 *
 * Subcommands:
 *   canary spec validate <file>   — parse + lint, exit non-zero on errors
 *   canary spec compile  <file>   — validate, then POST to /workflows/create-with-nodes
 *   canary spec pull <id>         — reverse: workflow → spec (TODO)
 *   canary spec diff <file> <id>  — what would compile change? (TODO)
 *
 * @module spec
 */

import fs from 'node:fs/promises';
import path from 'node:path';
import process from 'node:process';
import { getArgValue, hasFlag, resolveAppUrl } from '../auth.js';
import { fetchOrExit } from '../api-fetch.js';
import { readableSpecDirs, specDir, specExtension } from '../brand.js';
import {
  ApiSequenceFileSchema,
  diffWorkflow,
  emitSpec,
  extractPreservedFrontmatter,
  hasAnyChange,
  lintSpec,
  parseSpec,
  summarize,
  workflowToSpec,
  writeNodeIds,
  writeWorkflowId,
  type DiffResult,
  type LiveWorkflow,
  type PullWorkflow,
  type PullOptions,
  type SpecAst,
  type SpecIssue,
} from '@chatsdet/canary-spec';
import { applyDiff } from './sync.js';
import { mergeSpecRoots, slugify, stampedSpecPath, uniqueName } from './files.js';
export { indexSpecFiles, mergeSpecRoots, stampedSpecPath, uniqueName } from './files.js';

export async function handleSpec(argv: string[], apiUrl: string, token: string): Promise<void> {
  const sub = argv[0];
  switch (sub) {
    case 'validate':
      await handleValidate(argv.slice(1));
      return;
    case 'compile':
      await handleCompile(argv.slice(1), apiUrl, token);
      return;
    case 'pull':
      await handlePull(argv.slice(1), apiUrl, token);
      return;
    case 'diff':
      await handleDiff(argv.slice(1), apiUrl, token);
      return;
    default:
      printSpecHelp();
      process.exit(sub ? 1 : 0);
  }
}

function printSpecHelp(): void {
  console.log(
    [
      'Usage: canary spec <subcommand> <file>',
      '',
      'Subcommands:',
      '  validate <file>           Parse + lint the spec offline',
      '  compile  <file>           Validate, then create the workflow',
      '  diff     <file>           Preview what `compile` would change (no writes)',
      `  pull     <workflow-id>    Reverse: workflow → round-trippable ${specExtension}`,
      '  pull     --all            Sync a directory of specs down from all workflows',
      '',
      'Compile flags:',
      '  --allow-duplicate         Fresh compile: create even when a same-named workflow exists',
      '  --create-with-duplicate-title',
      '                            Alias for --allow-duplicate',
      '  --json                    Machine-readable output',
      '',
      'Pull flags:',
      `  --output <path|dir>       File (single) or directory (--all); default ${specDir}/`,
      '  --all                     Sync every workflow into the directory (add new, update matching by workflowId)',
      '  --status <s>              With --all, filter workflows by status (draft|approved|…)',
      '  --force                   Overwrite an existing file (single pull)',
    ].join('\n')
  );
}

async function handleValidate(argv: string[]): Promise<void> {
  const file = argv[0];
  if (!file) {
    console.error('Error: canary spec validate <file>');
    process.exit(1);
  }
  const json = hasFlag(argv, '--json');

  const result = await loadAndValidate(file);
  if (json) {
    console.log(
      JSON.stringify(
        {
          ok: result.ok,
          issues: result.issues,
          ...(result.ok ? { workflowName: result.ast.frontmatter.name } : {}),
        },
        null,
        2
      )
    );
    process.exit(result.ok ? 0 : 1);
  }
  printIssues(result.issues, file);
  if (!result.ok) {
    process.exit(1);
  }
  console.log(`✓ ${file} validates`);
}

async function handleCompile(argv: string[], apiUrl: string, token: string): Promise<void> {
  const file = argv[0];
  if (!file) {
    console.error('Error: canary spec compile <file>');
    process.exit(1);
  }
  const result = await loadAndValidate(file);
  printIssues(result.issues, file);
  if (!result.ok) {
    process.exit(1);
  }

  const ast = result.ast;
  // Append a default end node only on a fresh create; on recompile (spec
  // already bound to a workflow) suppress it so an end-less workflow doesn't
  // gain a phantom node on every round-trip.
  const emitted = emitSpec(ast, { appendDefaultEnd: !ast.frontmatter.workflowId });

  // Load any api_sequence sidecar referenced by a node. Multiple nodes pointing
  // at the same path share one sidecar (DB enforces one api_sequence row per
  // workflow); divergent paths produce a clear error before any state changes.
  try {
    emitted.apiSequenceConfig = await loadApiSequenceSidecar(emitted.nodes, file);
  } catch (err) {
    console.error(`Error: ${err instanceof Error ? err.message : String(err)}`);
    process.exit(1);
  }

  // Branch: recompile (frontmatter has `workflowId`) vs fresh create.
  if (emitted.workflowId) {
    await runRecompile({ argv, apiUrl, token, file, emitted });
    return;
  }

  await runFreshCompile({ argv, apiUrl, token, file, emitted });
}

/**
 * Resolve and load the api_sequence sidecar(s) referenced by spec nodes.
 *
 * The DB enforces one api_sequence row per workflow, so a single workflow may
 * carry many `nodeType: 'api_sequence'` nodes but they all point at the same
 * sidecar. We accept multiple identical paths but reject divergent ones with
 * a clear error before any state mutation.
 */
async function loadApiSequenceSidecar(
  nodes: ReadonlyArray<{ nodeType: string; configJson: Record<string, unknown> }>,
  specFile: string
): Promise<unknown> {
  const refs = nodes
    .filter((n) => n.nodeType === 'api_sequence')
    .map((n) => n.configJson['__apiSequenceSidecar'])
    .filter((p): p is string => typeof p === 'string' && p.length > 0);
  if (refs.length === 0) return undefined;
  const unique = Array.from(new Set(refs));
  if (unique.length > 1) {
    throw new Error(
      `Spec has divergent api_sequence sidecar paths: ${unique
        .map((p) => `"${p}"`)
        .join(', ')}. The DB stores one config per workflow — pick a single path.`
    );
  }
  const sidecarPath = path.resolve(path.dirname(specFile), unique[0]);
  let raw: string;
  try {
    raw = await fs.readFile(sidecarPath, 'utf-8');
  } catch (err) {
    throw new Error(
      `Could not read api_sequence sidecar at ${sidecarPath}: ${err instanceof Error ? err.message : String(err)}`
    );
  }
  const yaml = await import('yaml');
  let parsed: unknown;
  try {
    parsed = yaml.parse(raw);
  } catch (err) {
    throw new Error(
      `Sidecar YAML at ${sidecarPath} is invalid: ${err instanceof Error ? err.message : String(err)}`
    );
  }
  const validated = ApiSequenceFileSchema.safeParse(parsed);
  if (!validated.success) {
    throw new Error(
      `Sidecar at ${sidecarPath} doesn't match the api_sequence shape: ${validated.error.message}`
    );
  }
  return validated.data;
}

async function handleDiff(argv: string[], apiUrl: string, token: string): Promise<void> {
  const file = argv[0];
  if (!file) {
    console.error('Error: canary spec diff <file>');
    process.exit(1);
  }
  const result = await loadAndValidate(file);
  printIssues(result.issues, file);
  if (!result.ok) process.exit(1);

  const emitted = emitSpec(result.ast, { appendDefaultEnd: false });
  if (!emitted.workflowId) {
    console.log(
      `ℹ ${file} has no workflowId in frontmatter — \`canary spec compile\` would create a new workflow.`
    );
    console.log(`  Nodes that would be created: ${emitted.nodes.length}`);
    return;
  }

  const liveRes = await fetchOrExit(`${apiUrl}/workflows/${emitted.workflowId}`, {
    method: 'GET',
    headers: { Authorization: `Bearer ${token}` },
    token,
  });
  if (!liveRes.ok) {
    console.error(`Error: failed to fetch workflow ${emitted.workflowId} (${liveRes.status})`);
    process.exit(1);
  }
  const liveBody = (await liveRes.json().catch(() => ({}))) as { workflow?: LiveWorkflow };
  const live = liveBody.workflow;
  if (!live) {
    console.error(`Error: workflow ${emitted.workflowId} not found.`);
    process.exit(1);
  }

  let diff: DiffResult;
  try {
    diff = diffWorkflow(emitted, live);
  } catch (err) {
    console.error(`Error: ${err instanceof Error ? err.message : String(err)}`);
    process.exit(1);
  }

  if (hasFlag(argv, '--json')) {
    console.log(JSON.stringify({ ok: true, summary: summarize(diff), diff }, null, 2));
    return;
  }

  if (!hasAnyChange(diff)) {
    console.log(`✓ ${file} — in sync with workflow ${emitted.workflowId}`);
    return;
  }
  const s = summarize(diff);
  console.log(`\nPreview for "${live.name}" (${emitted.workflowId}):`);
  console.log(
    `  +${s.created} create  ·  ~${s.updated} update  ·  -${s.deleted} delete`
  );
  if (diff.nameDrift) console.log(`  ✎ rename: "${diff.nameDrift.from}" → "${diff.nameDrift.to}"`);
  const fieldKeys = Object.keys(diff.workflowFieldChanges).filter((k) => k !== 'name');
  if (fieldKeys.length > 0) console.log(`  ✎ workflow fields: ${fieldKeys.join(', ')}`);
  console.log(`\nRun \`canary spec compile ${file}\` to apply.`);
}

async function handlePull(argv: string[], apiUrl: string, token: string): Promise<void> {
  if (hasFlag(argv, '--all')) {
    await handlePullAll(argv, apiUrl, token);
    return;
  }

  const workflowId = argv.find((a) => !a.startsWith('-'));
  if (!workflowId) {
    console.error('Error: canary spec pull <workflow-id> [--output <path>] [--force]');
    console.error('       canary spec pull --all [--output <dir>] [--status <s>] [--force]');
    process.exit(1);
  }
  const json = hasFlag(argv, '--json');
  const force = hasFlag(argv, '--force');

  const live = await fetchWorkflow(apiUrl, token, workflowId);
  if (!live) {
    console.error(`Error: workflow ${workflowId} not found (or no permission).`);
    process.exit(1);
  }

  const outPath =
    getArgValue(argv, '--output') ??
    path.join(specDir, `${slugify(live.name)}${specExtension}`);

  // Preserve spec-source-only frontmatter from the prior file (requirementRef,
  // scenario skipReason). These have no live equivalent, so a naive overwrite
  // would silently drop the author's notes on re-pull.
  const preserved = await readPreservedFrontmatter(outPath);
  // When the workflow has an api_sequence config, fetch it + write a sidecar
  // file alongside the spec. Pull annotates each api_sequence node in the live
  // workflow with `__apiSequenceSidecar` so `workflowToSpec` can emit the
  // `- apiSequence:` bullet. The sidecar path lives next to the spec under a
  // sibling `sequences/` directory by convention.
  const sidecarPath = await fetchAndWriteApiSequenceSidecar(
    apiUrl,
    token,
    workflowId,
    live,
    outPath
  );
  if (sidecarPath) {
    for (const node of live.nodes) {
      if (node.nodeType === 'api_sequence') {
        node.configJson = {
          ...node.configJson,
          __apiSequenceSidecar: path.relative(path.dirname(outPath), sidecarPath),
        };
      }
    }
  }
  const source = workflowToSpec(live, preserved);

  // Validate the round-trip so a malformed pull surfaces immediately.
  const parsed = parseSpec(source);
  const lintIssues = parsed.ok ? lintSpec(parsed.ast) : [];
  const parseIssues = parsed.ok ? [] : parsed.issues;
  const issues = [...parseIssues, ...lintIssues];

  if (!force) {
    try {
      await fs.access(outPath);
      console.error(
        `Error: ${outPath} already exists. Use --output <path> or --force to overwrite.`
      );
      process.exit(1);
    } catch {
      // file does not exist — good.
    }
  }

  await fs.mkdir(path.dirname(outPath), { recursive: true });
  await fs.writeFile(outPath, source, 'utf-8');

  if (json) {
    console.log(JSON.stringify({ ok: true, file: outPath, workflowId, issues }, null, 2));
    return;
  }

  console.log(`\n✓ Pulled "${live.name}" → ${outPath}`);
  console.log(`  Nodes:  ${live.nodes.length}`);
  console.log(`  Edit the file, then \`canary spec compile ${outPath}\` to push changes back.`);
  if (issues.length > 0) {
    console.log('\n  Notes:');
    printIssues(issues, outPath);
  }
}

/**
 * `canary spec pull --all` — sync a directory of spec files (`.cofactor.md`,
 * legacy `.canary.md`) down from the live workflows. Existing files are matched
 * to a workflow by their `workflowId:` frontmatter and rewritten in place;
 * workflows with no matching file get a fresh `<slug>` spec in the active
 * brand's extension. Files are only written when their
 * content actually changes, so an already-synced repo produces no churn.
 */
async function handlePullAll(argv: string[], apiUrl: string, token: string): Promise<void> {
  const json = hasFlag(argv, '--json');
  const explicitDir = getArgValue(argv, '--output') ?? argv.find((a) => !a.startsWith('-'));
  // New files default to the active brand's dir (e.g. `cofactor/specs`).
  const dir = explicitDir ?? specDir;
  const status = getArgValue(argv, '--status');

  const workflows = await fetchWorkflowList(apiUrl, token, status);
  // Map existing files by their declared workflowId so re-pulls land in place.
  // With no explicit dir, scan every recognized spec root so an existing
  // `canary/specs` repo is matched and rewritten in place rather than
  // duplicated under the new default `cofactor/specs`.
  const indexDirs = explicitDir ? [explicitDir] : readableSpecDirs();
  const byWorkflowId = await mergeSpecRoots(indexDirs);
  const usedNames = new Set<string>([...byWorkflowId.values()].map((p) => path.basename(p)));

  const results: Array<{
    id: string;
    name: string;
    file: string;
    action: 'added' | 'synced' | 'unchanged' | 'failed';
    note?: string;
  }> = [];

  for (const wf of workflows) {
    const live = await fetchWorkflow(apiUrl, token, wf.id);
    if (!live) {
      results.push({ id: wf.id, name: wf.name, file: '', action: 'failed', note: 'fetch failed' });
      continue;
    }
    const existing = byWorkflowId.get(wf.id);
    let target: string;
    let isNew = false;
    if (existing) {
      target = existing;
    } else {
      target = path.join(dir, uniqueName(slugify(live.name), wf.id, usedNames));
      usedNames.add(path.basename(target));
      isNew = true;
    }

    let prior: string | null = null;
    try {
      prior = await fs.readFile(target, 'utf-8');
    } catch {
      // no prior file
    }
    // Spec-source-only fields (requirementRef, scenario skipReason) have no
    // server-side representation — lift them from the prior file before
    // emitting, so re-pull doesn't drop them.
    const preserved = prior ? extractPreservedFrontmatter(prior) : {};
    const source = workflowToSpec(live, preserved);
    if (prior === source) {
      results.push({ id: wf.id, name: live.name, file: target, action: 'unchanged' });
      continue;
    }
    await fs.mkdir(path.dirname(target), { recursive: true });
    await fs.writeFile(target, source, 'utf-8');
    results.push({ id: wf.id, name: live.name, file: target, action: isNew ? 'added' : 'synced' });
  }

  if (json) {
    console.log(JSON.stringify({ ok: true, dir, results }, null, 2));
    return;
  }
  const count = (a: string) => results.filter((r) => r.action === a).length;
  console.log(`\n✓ Synced ${results.length} workflow(s) → ${dir}`);
  console.log(
    `  +${count('added')} added  ·  ~${count('synced')} synced  ·  =${count('unchanged')} unchanged${count('failed') ? `  ·  ✗${count('failed')} failed` : ''}`
  );
  for (const r of results) {
    if (r.action === 'unchanged') continue;
    const mark = r.action === 'added' ? '+' : r.action === 'synced' ? '~' : '✗';
    console.log(
      `  ${mark} ${path.relative(process.cwd(), r.file) || r.file}${r.note ? `  (${r.note})` : ''}`
    );
  }
}

/** Fetch the full live workflow (with nodes + edges) for the diff/pull path. */
/**
 * Fetch the workflow's api_sequence config (if any) and write it to a sidecar
 * YAML alongside the spec. Returns the absolute path to the sidecar, or null
 * when the workflow doesn't have an api_sequence row.
 *
 * The sidecar filename is derived from the workflow slug and lands in a
 * sibling `sequences/` directory: `<spec-dir>/sequences/<slug>.yaml`.
 * Re-pull overwrites the file in place.
 */
async function fetchAndWriteApiSequenceSidecar(
  apiUrl: string,
  token: string,
  workflowId: string,
  live: PullWorkflow,
  outPath: string
): Promise<string | null> {
  // Skip when the workflow has no api_sequence nodes.
  if (!live.nodes.some((n) => n.nodeType === 'api_sequence')) return null;
  const res = await fetchOrExit(`${apiUrl}/api-sequences/by-workflow/${workflowId}`, {
    method: 'GET',
    headers: { Authorization: `Bearer ${token}` },
    token,
  });
  if (!res.ok) {
    // Workflow may have api_sequence nodes but no row yet (e.g. legacy state);
    // leave the sidecar off so the round-trip stays graceful.
    return null;
  }
  const body = (await res.json().catch(() => ({}))) as { data?: { config?: unknown } };
  const config = body.data?.config;
  if (!config) return null;

  const sequencesDir = path.join(path.dirname(outPath), 'sequences');
  const sidecarSlug = slugify(live.name);
  const sidecarPath = path.join(sequencesDir, `${sidecarSlug}.yaml`);
  await fs.mkdir(sequencesDir, { recursive: true });
  const yaml = await import('yaml');
  await fs.writeFile(sidecarPath, yaml.stringify(config), 'utf-8');
  return sidecarPath;
}

/**
 * Read spec-source-only frontmatter from the prior file at `target` (if any).
 * Returns an empty `PullOptions` when no prior file exists.
 */
async function readPreservedFrontmatter(target: string): Promise<PullOptions> {
  try {
    const prior = await fs.readFile(target, 'utf-8');
    return extractPreservedFrontmatter(prior);
  } catch {
    return {};
  }
}

async function fetchWorkflow(
  apiUrl: string,
  token: string,
  workflowId: string
): Promise<PullWorkflow | null> {
  const res = await fetchOrExit(`${apiUrl}/workflows/${workflowId}`, {
    method: 'GET',
    headers: { Authorization: `Bearer ${token}` },
    token,
  });
  if (!res.ok) return null;
  const body = (await res.json().catch(() => ({}))) as { workflow?: PullWorkflow };
  return body.workflow ?? null;
}

/** List workflows (paginated), optionally filtered by status. */
async function fetchWorkflowList(
  apiUrl: string,
  token: string,
  status: string | undefined
): Promise<Array<{ id: string; name: string }>> {
  const out: Array<{ id: string; name: string }> = [];
  let page = 1;
  for (;;) {
    const params = new URLSearchParams({ page: String(page), pageSize: '100' });
    if (status) params.set('status', status);
    const res = await fetchOrExit(`${apiUrl}/workflows?${params.toString()}`, {
      method: 'GET',
      headers: { Authorization: `Bearer ${token}` },
      token,
    });
    if (!res.ok) {
      console.error(`Error: failed to list workflows (${res.status})`);
      process.exit(1);
    }
    const body = (await res.json().catch(() => ({}))) as {
      data?: Array<{ id: string; name: string }>;
      pagination?: { page: number; totalPages: number };
    };
    for (const w of body.data ?? []) out.push({ id: w.id, name: w.name });
    const pg = body.pagination;
    if (!pg || pg.page >= pg.totalPages || (body.data ?? []).length === 0) break;
    page += 1;
  }
  return out;
}

async function runFreshCompile(opts: {
  argv: string[];
  apiUrl: string;
  token: string;
  file: string;
  emitted: ReturnType<typeof emitSpec>;
}): Promise<void> {
  const { argv, apiUrl, token, file, emitted } = opts;

  const duplicateMatches = await fetchFreshCompileDuplicateWorkflows({
    argv,
    apiUrl,
    token,
    workflowName: emitted.name,
  });
  if (duplicateMatches.length > 0) {
    const message = formatDuplicateWorkflowTitleWarning({
      file,
      workflowName: emitted.name,
      matches: duplicateMatches,
    });
    if (hasFlag(argv, '--json')) {
      console.log(
        JSON.stringify(
          {
            ok: false,
            error: 'duplicate_workflow_title',
            message,
            workflows: duplicateMatches,
          },
          null,
          2
        )
      );
    } else {
      console.error(message);
    }
    process.exit(1);
  }

  // `--publish` was removed with the approval/eligibility lifecycle split — approval
  // is an explicit human act (canary workflow approve) and suite inclusion is derived
  // eligibility (approved AND passed at least once). Flows always compile as drafts.
  if (hasFlag(argv, '--publish')) {
    console.error(
      'Error: --publish was removed. Compile the spec, then approve it ' +
        '(canary workflow approve <id> --yes); it becomes eligible for suites once it has passed.'
    );
    process.exit(1);
  }
  const status = emitted.status ?? 'draft';

  const payload: Record<string, unknown> = {
    name: emitted.name,
    description: emitted.description,
    preflight: emitted.preflight,
    cleanup: emitted.cleanup,
    status,
    flowType: emitted.flowType,
    createdVia: 'cli-spec',
    // The route only knows about (nodeType, configJson) — strip our meta.
    nodes: emitted.nodes.map(({ nodeType, configJson }) => ({ nodeType, configJson })),
    ...(emitted.inputsJson.length > 0 ? { inputsJson: emitted.inputsJson } : {}),
    ...(emitted.scenariosJson.length > 0 ? { scenariosJson: emitted.scenariosJson } : {}),
  };

  const res = await fetchOrExit(`${apiUrl}/workflows/create-with-nodes`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    token,
    body: JSON.stringify(payload),
  });

  const body = (await res.json().catch(() => ({}))) as {
    ok: boolean;
    error?: string;
    warnings?: string[];
    workflow?: { id: string; name: string; status: string };
    nodes?: Array<{ id: string; nodeType: string; orderIndex: number }>;
  };

  if (!res.ok || !body.ok || !body.workflow) {
    console.error(`Error: ${body.error ?? `${res.status} ${res.statusText}`}`);
    process.exit(1);
  }

  // Write workflowId back to frontmatter + @id comments to each lead-in line.
  // Pair each emitted node with its sourceLine and the corresponding new
  // server-side node UUID (the route returns nodes in orderIndex order).
  const writebackPairs: Array<{ sourceLine: number; nodeId: string }> = [];
  if (body.nodes && body.nodes.length === emitted.nodes.length) {
    for (let i = 0; i < emitted.nodes.length; i++) {
      const sourceLine = emitted.nodes[i].sourceLine;
      if (sourceLine === undefined) continue;
      writebackPairs.push({ sourceLine, nodeId: body.nodes[i].id });
    }
  }

  try {
    const source = await fs.readFile(file, 'utf-8');
    const withWorkflowId = writeWorkflowId(source, body.workflow.id);
    // writeWorkflowId may insert a new frontmatter line, shifting all subsequent
    // line numbers by +1. Shift sourceLine accordingly so node @id markers land
    // on the lead-in (not on the blank line before it).
    const lineShift = withWorkflowId.split('\n').length - source.split('\n').length;
    const shiftedPairs = writebackPairs.map((p) => ({
      ...p,
      sourceLine: p.sourceLine + lineShift,
    }));
    const next = writeNodeIds(withWorkflowId, shiftedPairs);
    if (next !== source) {
      await fs.writeFile(file, next, 'utf-8');
    }
  } catch (err) {
    console.warn(
      `⚠ Workflow created, but failed to write IDs back to ${file}: ${err instanceof Error ? err.message : String(err)}`
    );
  }

  if (hasFlag(argv, '--json')) {
    console.log(JSON.stringify({ ok: true, workflow: body.workflow, writebackPairs }, null, 2));
    return;
  }

  const appUrl = resolveAppUrl(apiUrl);
  console.log(`\n✓ Spec compiled: ${body.workflow.name}`);
  console.log(`  ID:     ${body.workflow.id}`);
  console.log(`  Status: ${body.workflow.status}`);
  console.log(`  Nodes:  ${body.nodes?.length ?? 0}`);
  console.log(`  URL:    ${appUrl}/flows/${body.workflow.id}`);
  console.log(`  → wrote workflowId + @id markers back to ${file}`);
  if (body.warnings && body.warnings.length > 0) {
    console.log('\n  Warnings:');
    for (const w of body.warnings) console.log(`    ⚠ ${w}`);
  }

  if (hasFlag(argv, '--run-and-publish')) {
    console.log(
      '\nℹ --run-and-publish was removed. Run and approve; it becomes eligible for suites once it has passed:'
    );
    console.log(`  canary workflow run ${body.workflow.id}`);
    console.log(`  canary workflow approve ${body.workflow.id} --yes`);
  }
}

export const DUPLICATE_WORKFLOW_TITLE_ALLOW_FLAGS = [
  '--allow-duplicate',
  '--create-with-duplicate-title',
] as const;

export type WorkflowTitleMatch = {
  id: string;
  name: string;
  status?: string;
  flowType?: string;
};

export function allowsDuplicateWorkflowTitle(argv: string[]): boolean {
  return DUPLICATE_WORKFLOW_TITLE_ALLOW_FLAGS.some((flag) => hasFlag(argv, flag));
}

export function exactWorkflowTitleMatches(
  workflows: ReadonlyArray<WorkflowTitleMatch>,
  workflowName: string
): WorkflowTitleMatch[] {
  return workflows.filter((workflow) => workflow.name === workflowName);
}

export function formatDuplicateWorkflowTitleWarning(opts: {
  file: string;
  workflowName: string;
  matches: ReadonlyArray<WorkflowTitleMatch>;
}): string {
  const first = opts.matches[0];
  const stampedFile = stampedSpecPath(opts.file);
  const workflowLines = opts.matches
    .map((workflow) => {
      const details = [workflow.status, workflow.flowType].filter(Boolean).join(', ');
      return `  - ${workflow.id}${details ? ` (${details})` : ''}`;
    })
    .join('\n');
  return [
    `Error: fresh spec compile would create a duplicate workflow named "${opts.workflowName}".`,
    '',
    'Existing workflow(s):',
    workflowLines,
    '',
    'To update an existing workflow instead, pull a stamped spec, re-apply your edits, then compile it:',
    `  canary spec pull ${first.id} --output ${stampedFile}`,
    `  canary spec compile ${stampedFile}`,
    '',
    'To intentionally create another workflow with the same title, re-run with:',
    `  canary spec compile ${opts.file} --allow-duplicate`,
  ].join('\n');
}

async function fetchFreshCompileDuplicateWorkflows(opts: {
  argv: string[];
  apiUrl: string;
  token: string;
  workflowName: string;
}): Promise<WorkflowTitleMatch[]> {
  if (allowsDuplicateWorkflowTitle(opts.argv)) return [];
  const params = new URLSearchParams({
    search: opts.workflowName,
    pageSize: '50',
  });
  const res = await fetchOrExit(`${opts.apiUrl}/workflows?${params.toString()}`, {
    method: 'GET',
    headers: { Authorization: `Bearer ${opts.token}` },
    token: opts.token,
  });
  if (!res.ok) {
    console.error(`Error: failed to check for existing workflow titles (${res.status})`);
    process.exit(1);
  }
  const body = (await res.json().catch(() => ({}))) as {
    data?: WorkflowTitleMatch[];
  };
  return exactWorkflowTitleMatches(body.data ?? [], opts.workflowName);
}

async function runRecompile(opts: {
  argv: string[];
  apiUrl: string;
  token: string;
  file: string;
  emitted: ReturnType<typeof emitSpec>;
}): Promise<void> {
  const { argv, apiUrl, token, file, emitted } = opts;
  const workflowId = emitted.workflowId!;

  // 1. Fetch live workflow. GET /workflows/:id returns the workflow with
  //    nodes + edges via getWorkflowWithDetails.
  const liveRes = await fetchOrExit(`${apiUrl}/workflows/${workflowId}`, {
    method: 'GET',
    headers: { Authorization: `Bearer ${token}` },
    token,
  });
  if (!liveRes.ok) {
    console.error(`Error: failed to fetch workflow ${workflowId} (${liveRes.status})`);
    process.exit(1);
  }
  const liveBody = (await liveRes.json().catch(() => ({}))) as {
    workflow?: LiveWorkflow & { snapshotVersion?: number };
  };
  const live = liveBody.workflow;
  if (!live) {
    console.error(`Error: workflow ${workflowId} not found (or no permission).`);
    process.exit(1);
  }

  // 2. Compute the diff.
  let diff: DiffResult;
  try {
    diff = diffWorkflow(emitted, live);
  } catch (err) {
    console.error(`Error: ${err instanceof Error ? err.message : String(err)}`);
    process.exit(1);
  }

  if (!hasAnyChange(diff)) {
    console.log(`✓ ${file} — no changes (workflow ${workflowId} already in sync)`);
    return;
  }

  // 3. Apply.
  const ctx = { apiUrl, token, workflowId };
  const sync = await applyDiff(ctx, diff, emitted, live);

  // 4. Write back any new @id comments.
  if (sync.created.length > 0) {
    try {
      const source = await fs.readFile(file, 'utf-8');
      const next = writeNodeIds(source, sync.created);
      if (next !== source) await fs.writeFile(file, next, 'utf-8');
    } catch (err) {
      console.warn(
        `⚠ Recompile applied, but failed to write new @id markers to ${file}: ${err instanceof Error ? err.message : String(err)}`
      );
    }
  }

  // 5. Summary.
  const s = summarize(diff);
  const appUrl = resolveAppUrl(apiUrl);
  console.log(`\n✓ Recompiled "${live.name}"`);
  console.log(
    `  +${s.created} created  ·  ~${s.updated} updated  ·  -${s.deleted} deleted`
  );
  console.log(`  URL: ${appUrl}/flows/${workflowId}`);
  if (diff.nameDrift) {
    console.log(`  ✎ renamed: "${diff.nameDrift.from}" → "${diff.nameDrift.to}"`);
  }
  if (live.paused !== true) {
    console.log(
      `  ⚠ workflow is not paused — if it is eligible, suites run the live definition, so these changes take effect on the next suite run.`
    );
  }
  if (diff.duplicateSpecIds.length > 0) {
    console.log(
      `  ⚠ duplicate @id in spec (treated as fresh creates): ${diff.duplicateSpecIds.join(', ')}`
    );
  }
  if (diff.orphanedSpecIds.length > 0) {
    console.log(
      `  ⚠ stale @id in spec (no matching node — rebound to new): ${diff.orphanedSpecIds.join(', ')}`
    );
  }

  if (hasFlag(argv, '--json')) {
    console.log(JSON.stringify({ ok: true, workflowId, summary: s, diff }, null, 2));
  }
}

type LoadResult =
  | { ok: true; ast: SpecAst; issues: SpecIssue[] }
  | { ok: false; issues: SpecIssue[] };

async function loadAndValidate(file: string): Promise<LoadResult> {
  let source: string;
  try {
    source = await fs.readFile(file, 'utf-8');
  } catch (err) {
    return {
      ok: false,
      issues: [
        {
          severity: 'error',
          message: `Could not read ${file}: ${err instanceof Error ? err.message : String(err)}`,
        },
      ],
    };
  }
  const parsed = parseSpec(source);
  if (!parsed.ok) return { ok: false, issues: parsed.issues };
  const lintIssues = lintSpec(parsed.ast);
  const allIssues = [...lintIssues];
  const hasError = allIssues.some((i) => i.severity === 'error');
  if (hasError) return { ok: false, issues: allIssues };
  return { ok: true, ast: parsed.ast, issues: allIssues };
}

function printIssues(issues: SpecIssue[], file: string): void {
  if (issues.length === 0) return;
  for (const i of issues) {
    const prefix = i.severity === 'error' ? '✗ error' : '⚠ warn';
    const loc = i.line ? `${file}:${i.line}` : file;
    console.error(`${prefix} ${loc}: ${i.message}`);
    if (i.hint) console.error(`         hint: ${i.hint}`);
  }
}

export { handleValidate, handleCompile };

// Used by tests.
export { emitSpec, lintSpec, parseSpec };
